/**
 * Carpets & Rugs — the browser half of the carpet price.
 *
 * Mirrors `OrderService::computeCarpetLineCost()` in urbansofa-api. The two must
 * agree to the cent (FR-019); `carpet-price.spec.ts` asserts the same fixture the
 * PHP test uses, so drift shows up in test output rather than on a dealer order.
 *
 * The rules that are easy to get wrong:
 *  - round UP, once, on the per-unit totals only — never per component (FR-013)
 *  - strip VAT once, from the customer scope only (FR-016)
 *  - quantity multiplies the whole per-unit total, shape included (FR-014)
 *  - colour never contributes (FR-010)
 */

export interface CarpetRates {
  /** Product rate per m². Dealer excl. VAT, customer incl. VAT — as entered */
  materialDealer: number;
  materialCustomer: number;
  /** Shape option: flat, once per unit */
  shapeDealer?: number;
  shapeCustomer?: number;
  /** Finishing option: per linear metre of perimeter */
  finishingDealer?: number;
  finishingCustomer?: number;
  /**
   * Fixed price for this preset size, replacing the MATERIAL LEG ONLY — see the bypass
   * comment in computeCarpetLineCost(). Resolve them with resolveFixedSizePrice() and
   * pass both or neither; dealer excl. VAT, customer incl. VAT, as everywhere else.
   */
  fixedMaterialDealer?: number | null;
  fixedMaterialCustomer?: number | null;
}

/** A preset size as the order screen receives it, via the order_optimize_view group. */
export interface CarpetPresetSize {
  widthCm: number;
  lengthCm: number;
  fixedDealerPrice?: number | string | null;
  fixedCustomerPrice?: number | string | null;
}

export interface CarpetLineCost {
  areaM2: number;
  perimeterM: number;
  /**
   * Per-unit component amounts, for the itemised price box. A BREAKDOWN of
   * `perUnit*` — material carries the line's round-up remainder, so the rows
   * always add up to the amount charged (D6).
   */
  materialDealer: number;
  materialCustomer: number;
  shapeDealer: number;
  shapeCustomer: number;
  finishingDealer: number;
  finishingCustomer: number;
  perUnitDealerExclVat: number;
  perUnitCustomerInclVat: number;
  perUnitCustomerExclVat: number;
  perUnitCustomerVat: number;
  /** True when a fixed preset price replaced the material leg — drives the row label */
  bypassed: boolean;
  dealer: number;
  customerInclVat: number;
  customerExclVat: number;
  customerVat: number;
}

/**
 * Round up to the cent, matching `NumberTrait::roundUp()` on the server:
 * scale by 100, round to 6 decimal places to absorb IEEE-754 binary noise,
 * then ceil.
 *
 * e.g. 1.00 × 1.20 m at €72.40/m² is exactly 86.88, but lands on
 * 86.88000000000001 in binary. Ceiling that raw double promotes it to a
 * phantom €86.89. Rounding the scaled value to 6 decimals first collapses the
 * noise back to 8688.000000, so the ceil correctly lands on €86.88.
 *
 * If this is ever changed, change `NumberTrait::roundUp()` on the server in
 * the same commit — FR-019 requires the two agree to the cent.
 */
function ceilToCent(value: number): number {
  const scaled = Math.round(value * 100 * 1e6) / 1e6;
  return Math.ceil(scaled) / 100;
}

/** Nearest cent — what the currency pipes display, so the breakdown rows agree (D6) */
function toCent(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * The fixed price to use as this line's material leg, or null for the per-m² formula.
 *
 * FIXED PRICE PER PRESET SIZE — deliberate technical debt, client change request of
 * 2026-08-28 (US-597 / US-596). Fixed-size rugs are mass-produced and sell below their
 * calculated area × rate price, so a preset may carry its own dealer and customer price
 * that replaces the material leg. This reverses US-508's "no price column, ever"; that
 * reasoning was correct and still is for every preset that does not explicitly opt in.
 *
 * Resolution is by DIMENSIONS, never by which preset the dealer selected. The order line
 * stores only widthCm/lengthCm — no ProductSize reference reaches the server
 * (`OrderItemProduct.php:45,52`), and none should be added. So the server matches on
 * dimensions in `OrderService::findFixedPriceProductSize()` and this runs the identical
 * lookup; any difference between the two means a dealer is shown one price and charged
 * another, which is scenario E4, the defect that took three test rounds to close.
 * Hiding the manual width/length fields on a priced preset is UX, not the pricing rule.
 *
 * The tie-break — "if two presets share dimensions and only one is priced, the priced one
 * wins" — falls out for free: only presets carrying BOTH prices are ever candidates.
 */
export function resolveFixedSizePrice(
  presets: CarpetPresetSize[] | null | undefined,
  widthCm: number,
  lengthCm: number
): {dealer: number, customer: number} | null {
  if (!presets || !(widthCm > 0) || !(lengthCm > 0)) {
    return null;
  }
  const match = presets.find(preset =>
    +preset.widthCm === +widthCm
    && +preset.lengthCm === +lengthCm
    && preset.fixedDealerPrice !== null && preset.fixedDealerPrice !== undefined && preset.fixedDealerPrice !== ''
    && preset.fixedCustomerPrice !== null && preset.fixedCustomerPrice !== undefined && preset.fixedCustomerPrice !== '');
  return match ? {dealer: +match.fixedDealerPrice, customer: +match.fixedCustomerPrice} : null;
}

export function computeCarpetLineCost(
  widthCm: number,
  lengthCm: number,
  rates: CarpetRates,
  vatPercent: number,
  quantity = 1
): CarpetLineCost | null {
  if (!(widthCm > 0) || !(lengthCm > 0)) {
    return null;
  }

  const widthM = widthCm / 100;
  const lengthM = lengthCm / 100;
  const areaM2 = widthM * lengthM;
  const perimeterM = 2 * (widthM + lengthM);

  const shapeDealer = rates.shapeDealer || 0;
  const shapeCustomer = rates.shapeCustomer || 0;
  const finishingDealerExact = perimeterM * (rates.finishingDealer || 0);
  const finishingCustomerExact = perimeterM * (rates.finishingCustomer || 0);

  // THE BYPASS. A fixed preset price replaces the MATERIAL LEG ONLY (PM decision 1,
  // 2026-09-07): shape and finishing still add on top, and the sum is still rounded up
  // once, exactly as for a formula-priced line. The configured price is never rounded on
  // its own — the round-up exists for the fractional cents the formula produces.
  //
  // The area is unused on a bypassed line, but the PERIMETER still is: finishing stays
  // perimeter × rate, so two priced presets of one product carry the same flat shape
  // surcharge and DIFFERENT finishing surcharges. Never cache one options total per
  // product and reuse it across presets.
  //
  // The server does the identical thing in CarpetPricingCalculator::calculatePerUnitRate(),
  // which takes the resolved price as a parameter for the same reason. Both sides must
  // change together — see resolveFixedSizePrice() above for why, and for the ticket trail.
  const bypassed = rates.fixedMaterialDealer !== null && rates.fixedMaterialDealer !== undefined
    && rates.fixedMaterialCustomer !== null && rates.fixedMaterialCustomer !== undefined;
  const materialDealerExact = bypassed ? rates.fixedMaterialDealer : areaM2 * (rates.materialDealer || 0);
  const materialCustomerExact = bypassed ? rates.fixedMaterialCustomer : areaM2 * (rates.materialCustomer || 0);

  // Round UP once, on the EXACT sum only — never on a pre-rounded component, or
  // the browser drifts from CarpetPricingCalculator on the server (FR-013/FR-019).
  const perUnitDealerExclVat = ceilToCent(materialDealerExact + shapeDealer + finishingDealerExact);
  const perUnitCustomerInclVat = ceilToCent(materialCustomerExact + shapeCustomer + finishingCustomerExact);

  // D6 "phantom cent": the itemised rows are a BREAKDOWN of the per-unit total, not
  // independent figures. Rendered raw they were rounded to the NEAREST cent by
  // CustomCurrencyPipe while the total above is CEILED, so a genuine fractional cent
  // made one line disagree with itself — 321 × 700 at €17,20/m² is €386,484, shown as
  // Material €386,48 next to Per rug €386,49. Quantise the fractional component and
  // let material carry the round-up remainder, so material + shape + finishing is
  // exactly the per-unit total, in both scopes, and matches what the server charges.
  //
  // This applies to a bypassed line too, so the material row can sit a cent above the
  // configured price when finishing produces a fractional cent. That is deliberate: the
  // server derives the same row the same way, by subtracting the carpet options from the
  // line total (CarpetLineMaterialPriceResolver), and the screen agreeing with the order
  // to the cent (E4) outranks the row reading back as the number that was typed.
  const finishingDealer = toCent(finishingDealerExact);
  const finishingCustomer = toCent(finishingCustomerExact);
  const materialDealer = toCent(perUnitDealerExclVat - shapeDealer - finishingDealer);
  const materialCustomer = toCent(perUnitCustomerInclVat - shapeCustomer - finishingCustomer);
  const perUnitCustomerExclVat = perUnitCustomerInclVat / (1 + (vatPercent || 0) / 100);
  const perUnitCustomerVat = perUnitCustomerInclVat - perUnitCustomerExclVat;

  const qty = quantity > 0 ? quantity : 1;

  return {
    areaM2,
    perimeterM,
    materialDealer,
    materialCustomer,
    shapeDealer,
    shapeCustomer,
    finishingDealer,
    finishingCustomer,
    perUnitDealerExclVat,
    perUnitCustomerInclVat,
    perUnitCustomerExclVat,
    perUnitCustomerVat,
    bypassed,
    dealer: perUnitDealerExclVat * qty,
    customerInclVat: perUnitCustomerInclVat * qty,
    customerExclVat: perUnitCustomerExclVat * qty,
    customerVat: perUnitCustomerVat * qty,
  };
}

/**
 * D9: the option name a rug's Shape / Finishing / Colour row shows, read straight off a
 * saved order. Mirrors `OrderService::carpetRoleOf()` on the server — the role sits on
 * the leaf Option, or on its parent group, so both are checked in that order.
 *
 * Read-only payloads expose the Option under its serialized property name,
 * `urbanOption`; the order screen hands around a richer `option` object while editing.
 * Accept either, so one reader serves every screen.
 */
export function carpetOptionName(orderItemOptions: any[], role: string): string {
  const match = (orderItemOptions || [])
    .map(row => (row && (row.urbanOption || row.option)) || null)
    .find(option => !!option && (option.carpetRole || (option.parent && option.parent.carpetRole)) === role);
  return match ? match.name : null;
}

/**
 * The label key for a carpet role's row.
 *
 * Shape and finishing have proper keys and are served in both languages. Colour does
 * NOT: `admin.dealer.order.carpet_colour` is used by the order screen and by the
 * service-request screens but was never added to the API's translation set, so it
 * printed its own key to the user next to correctly translated siblings — which reads
 * as a data problem rather than a missing string. `doctrine.fabric_color.color` is
 * served, in both languages ("Color" / "Kleur"), and says exactly the right word.
 *
 * Verified against the served catalogue, not the yml — the API serves translations from
 * its database, so a key present in `translations/*.yml` is not necessarily deliverable.
 * When `admin.dealer.order.carpet_colour` is added there, point colour back at it and
 * this whole function collapses to a template string again.
 */
export function carpetRoleLabelKey(role: string): string {
  return role === 'colour'
    ? 'doctrine.fabric_color.color'
    : `admin.dealer.order.carpet_${role}`;
}

/** "3.20 × 7.00 m" — metres, the convention the order screen's summary rows already use */
export function carpetSizeLabel(widthCm: number, lengthCm: number): string {
  return widthCm > 0 && lengthCm > 0
    ? `${(widthCm / 100).toFixed(2)} × ${(lengthCm / 100).toFixed(2)} m`
    : null;
}

/** The role a group carries — on the group itself, or on its leaves (both are configurable) */
export function carpetRoleOfGroup(option: any): string {
  if (!option) {
    return null;
  }
  if (option.carpetRole) {
    return option.carpetRole;
  }
  const child = (option.options || []).find(leaf => leaf && leaf.carpetRole);
  return (child && child.carpetRole) || null;
}

/**
 * The carpet role an option ROW carries (US-604).
 *
 * In production the role is configured on the parent GROUP, not on the leaf the dealer
 * picked, so a row is resolved through the group it was built from (`firstParent`).
 * `groups` is every carpet group the row could belong to, most trustworthy first — the
 * caller passes its elements' own groups ahead of the article's option list, because that
 * list is model/style- and deleted-filtered by a different endpoint, is still empty on the
 * first pricing pass, and is deleted outright before the save payload is built. A row whose
 * group was out of reach silently stopped being a carpet row: it was then neither stripped
 * on rebuild nor keyed to its element on save, which is how one role reached the payload
 * twice, both copies priced.
 *
 * A row being edited carries the Option under `option` and, after the payload flattens it,
 * as a bare id; a row read back from the API carries it under `urbanOption` with the group
 * nested as `parent`. All three resolve.
 */
export function carpetRoleOfRow(row: any, groups: any[]): string {
  const option = row && (row.option || row.urbanOption);
  const own = option && (option.carpetRole || (option.parent && option.parent.carpetRole));
  if (own) {
    return own;
  }
  const groupId = row && (row.firstParent || row.parent);
  if (!groupId) {
    return null;
  }
  const group = (groups || []).find(g => g && g.option && g.option.id === groupId);
  return carpetRoleOfGroup(group && group.option);
}

/**
 * The article's option rows that belong to ONE element.
 *
 * A rug's shape/finishing/colour are per element, but the order payload exposes the rows
 * at article level with only a Product id on them — two elements holding the same rug are
 * indistinguishable there. `orderItemOptionIds` is the per-element list the API reports
 * (OrderItemProduct::getOrderItemOptionIds), so it is what pairs a row to its element.
 *
 * With no ids to go on — an older payload, or a screen whose serialization group omits
 * them — the article-wide list is returned unchanged, which is the best the order-level
 * list can offer and matches the previous behaviour.
 */
export function carpetRowsOfElement(rows: any[], orderItemOptionIds: number[]): any[] {
  if (!orderItemOptionIds || !orderItemOptionIds.length) {
    return rows || [];
  }
  return (rows || []).filter(row => {
    const option = row && (row.urbanOption || row.option);
    return option && orderItemOptionIds.indexOf(option.id) > -1;
  });
}
